/**
 * The outcome of an undo - the planned one and the performed one, which are the
 * same shape and are therefore drawn by the same component.
 *
 * KEPT IS THE INTERESTING COLUMN. A deleted row is the undo doing what it said;
 * a kept row is the undo refusing, because something newer of ours points at
 * that row and taking it would take our own work with it. So the examples carry
 * `heldBy`: the operator does not need a number, they need to know which record
 * to go and look at.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { RevertResult } from "../../../../lib/crmImport";
import { fmtNum } from "../../../ui";

export function RevertReport({ result, planned }: { result: RevertResult; planned: boolean }) {
  const t = useT();
  // A model the ledger owns nothing in has nothing to say here, and eighteen
  // rows of zeroes would bury the two that matter.
  const models = result.models.filter((m) => m.owned > 0);
  const kept = models.flatMap((m) => m.keptExamples.map((ex) => ({ ...ex, model: m.model })));

  return (
    <div className="mt-4">
      <div className="crm-cost-stats">
        <div className="crm-cost-stat">
          <span className="crm-cost-stat-l">
            {t(planned ? "crmImport.revert.wouldDelete" : "crmImport.revert.deleted")}
          </span>
          <strong className="crm-cost-stat-v">{fmtNum(result.totalDeleted)}</strong>
        </div>
        <div className="crm-cost-stat">
          <span className="crm-cost-stat-l">
            {t(planned ? "crmImport.revert.wouldKeep" : "crmImport.revert.kept")}
          </span>
          <strong className="crm-cost-stat-v">{fmtNum(result.totalKept)}</strong>
          <span className="crm-cost-stat-n">{t("crmImport.revert.keptWhy")}</span>
        </div>
      </div>

      {models.length === 0 ? (
        <p className="crm-sub text-[12.5px]">{t("crmImport.revert.nothingOwned")}</p>
      ) : (
        <div className="crm-cov-scroll">
          <table className="crm-cov-tbl">
            <thead>
              <tr>
                <th scope="col">{t("crmImport.revert.model")}</th>
                <th scope="col">{t("crmImport.revert.owned")}</th>
                <th scope="col">{t(planned ? "crmImport.revert.wouldDelete" : "crmImport.revert.deleted")}</th>
                <th scope="col">{t(planned ? "crmImport.revert.wouldKeep" : "crmImport.revert.kept")}</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.model}>
                  <th scope="row">{m.model}</th>
                  <td>{fmtNum(m.owned)}</td>
                  <td>{fmtNum(m.deleted)}</td>
                  <td>{fmtNum(m.kept)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {kept.length > 0 ? (
        <div className="mt-4">
          <span className="crm-lab !mt-0">{t("crmImport.revert.keptExamples")}</span>
          <ul className="mt-1.5 space-y-1">
            {kept.map((ex) => (
              <li key={`${ex.model}:${ex.recordId}`} className="break-words text-[12px] leading-snug">
                <b className="font-semibold">{ex.model}</b>{" "}
                <span className="text-muted-foreground">{ex.recordId}</span>
                <span className="text-muted-foreground"> - {t("crmImport.revert.heldBy")} </span>
                <b className="font-semibold">{ex.heldBy}</b>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
